/**
 * Module #13 — Consignor settlement panel for the auction detail page.
 *
 * After an auction completes, staff:
 *   1. Generate statements  → public.generate_consignor_statements(auction)
 *      builds/refreshes one DRAFT statement per consignor with sold lots.
 *   2. Finalize             → public.finalize_consignor_statement(stmt)
 *      assigns the per-tenant statement number + locks the doc.
 *   3. Record payout        → public.record_consignor_payout(stmt, amount, …)
 *      records money paid + rolls balance/status forward (→ paid at zero).
 *
 * Each action is one atomic RPC (see migration 20260622120000). Renders only
 * once the auction is completed (or already has statements) so it stays out of
 * the way while an auction is still being set up.
 */

import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import { dollarsToCents, formatCents, type Tables } from '@auctisync/shared';
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  DollarInput,
  InlineError,
  SkeletonList,
  editorBoxStyle,
  inputStyle,
  listStyle,
  readRowStyle,
  selectStyle,
} from './profileKit';

type Statement = Tables<'consignor_statements'>;

const PAYMENT_TYPES = ['check', 'ach', 'cash', 'stripe', 'other'] as const;

const STATUS_BADGE: Record<string, 'secondary' | 'warning' | 'success' | 'outline'> = {
  draft: 'secondary',
  finalized: 'warning',
  paid: 'success',
  void: 'outline',
};

export function ConsignorSettlementPanel({
  auctionId,
  auctionStatus,
}: {
  auctionId: string;
  auctionStatus: string;
}) {
  const [statements, setStatements] = useState<Statement[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data: stmts, error: sErr } = await supabase
      .from('consignor_statements')
      .select('*')
      .eq('auction_id', auctionId)
      .order('statement_number', { ascending: true, nullsFirst: true });
    if (sErr) {
      setError(sErr.message);
      return;
    }
    const rows = stmts ?? [];
    setStatements(rows);

    const ids = [...new Set(rows.map((r) => r.consignor_id))];
    if (ids.length > 0) {
      const { data: cons } = await supabase
        .from('consignors')
        .select('id, display_name, tenant_consignor_number')
        .in('id', ids);
      const map: Record<string, string> = {};
      for (const c of cons ?? []) {
        map[c.id] = c.tenant_consignor_number
          ? `${c.display_name} (#${c.tenant_consignor_number})`
          : c.display_name;
      }
      setNames(map);
    }
  }, [auctionId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  async function handleGenerate() {
    if (busy) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc('generate_consignor_statements', {
      p_auction_id: auctionId,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    await refresh();
  }

  async function handleFinalize(id: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc('finalize_consignor_statement', {
      p_statement_id: id,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    await refresh();
  }

  // Hide entirely while the auction is still being built and nothing's settled.
  if (!loading && statements.length === 0 && auctionStatus !== 'completed') {
    return null;
  }

  return (
    <section style={{ marginTop: '2.5rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <div>
          <h2
            style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: '0.2rem',
            }}
          >
            Consignor settlement
          </h2>
          <p style={{ fontSize: '0.825rem', color: 'var(--text-muted)', margin: '0 0 0.35rem' }}>
            One statement per consignor with sold lots. Generate refreshes drafts; finalized and
            paid statements are never overwritten.
          </p>
          <LearnMoreLink helpSlug="consignor-settlement" newTab>
            About settlement
          </LearnMoreLink>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => void handleGenerate()}
        >
          {busy ? 'Working…' : statements.length > 0 ? 'Regenerate drafts' : 'Generate statements'}
        </Button>
      </div>

      {error && <InlineError>{error}</InlineError>}

      {loading ? (
        <SkeletonList />
      ) : statements.length === 0 ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          No statements yet. Generate them once lots are sold — each consignor with a sold lot gets
          a draft you can finalize and pay.
        </p>
      ) : (
        <ul style={{ ...listStyle, gap: '0.5rem' }}>
          {statements.map((s) => (
            <li key={s.id}>
              <StatementRow
                statement={s}
                consignorName={names[s.consignor_id] ?? 'Consignor'}
                busy={busy}
                paying={payingId === s.id}
                onFinalize={() => void handleFinalize(s.id)}
                onTogglePay={() => setPayingId((cur) => (cur === s.id ? null : s.id))}
                onPaid={async () => {
                  setPayingId(null);
                  await refresh();
                }}
                onError={setError}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatementRow({
  statement: s,
  consignorName,
  busy,
  paying,
  onFinalize,
  onTogglePay,
  onPaid,
  onError,
}: {
  statement: Statement;
  consignorName: string;
  busy: boolean;
  paying: boolean;
  onFinalize: () => void;
  onTogglePay: () => void;
  onPaid: () => Promise<void> | void;
  onError: (msg: string | null) => void;
}) {
  return (
    <div style={{ ...readRowStyle, flexDirection: 'column', alignItems: 'stretch', gap: '0.6rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ color: 'var(--text)' }}>{consignorName}</strong>
            <Badge variant={STATUS_BADGE[s.status] ?? 'outline'}>{s.status}</Badge>
            {s.statement_number && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {s.statement_number}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            {s.sold_lot_count} sold · sale {formatCents(s.sale_total_cents)} · commission{' '}
            {formatCents(s.commission_cents)} · fees {formatCents(s.fees_cents)}
          </div>
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: '0.9rem', color: 'var(--text)', fontWeight: 600 }}>
            Net due seller {formatCents(s.net_due_seller_cents)}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            paid {formatCents(s.amount_paid_cents)} · balance {formatCents(s.balance_cents)}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
        {s.status === 'draft' && (
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onFinalize}>
            Finalize
          </Button>
        )}
        {(s.status === 'finalized' || s.status === 'paid') && (
          <Button type="button" size="sm" variant="outline" onClick={onTogglePay}>
            {paying ? 'Cancel' : 'Record payout'}
          </Button>
        )}
      </div>

      {paying && (
        <PayoutForm
          statementId={s.id}
          defaultPayableTo={consignorName.replace(/\s*\(#.*\)$/, '')}
          balanceCents={s.balance_cents}
          onPaid={onPaid}
          onError={onError}
        />
      )}
    </div>
  );
}

function PayoutForm({
  statementId,
  defaultPayableTo,
  balanceCents,
  onPaid,
  onError,
}: {
  statementId: string;
  defaultPayableTo: string;
  balanceCents: number;
  onPaid: () => Promise<void> | void;
  onError: (msg: string | null) => void;
}) {
  const [amount, setAmount] = useState(balanceCents > 0 ? (balanceCents / 100).toFixed(2) : '');
  const [paymentType, setPaymentType] = useState<string>('check');
  const [checkNumber, setCheckNumber] = useState('');
  const [payableTo, setPayableTo] = useState(defaultPayableTo);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (saving) return;
    const cents = dollarsToCents(Number(amount));
    if (!Number.isFinite(cents) || cents <= 0) {
      onError('Enter a payout amount greater than zero.');
      return;
    }
    onError(null);
    setSaving(true);
    const checkNo = paymentType === 'check' ? checkNumber.trim() : '';
    const payTo = payableTo.trim();
    const noteText = note.trim();
    const { error: err } = await supabase.rpc('record_consignor_payout', {
      p_statement_id: statementId,
      p_amount_cents: cents,
      p_payment_type: paymentType,
      ...(checkNo ? { p_check_number: checkNo } : {}),
      ...(payTo ? { p_payable_to: payTo } : {}),
      ...(noteText ? { p_note: noteText } : {}),
    });
    setSaving(false);
    if (err) {
      onError(err.message);
      return;
    }
    await onPaid();
  }

  return (
    <div style={editorBoxStyle}>
      <div
        style={{
          display: 'grid',
          gap: '0.5rem',
          gridTemplateColumns: 'minmax(6rem,1fr) minmax(7rem,auto)',
        }}
      >
        <label style={{ display: 'grid', gap: '0.2rem' }}>
          <span style={labelStyle}>Amount</span>
          <DollarInput value={amount} onChange={setAmount} />
        </label>
        <label style={{ display: 'grid', gap: '0.2rem' }}>
          <span style={labelStyle}>Method</span>
          <select
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
            aria-label="Payment method"
            style={selectStyle}
          >
            {PAYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '1fr 1fr' }}>
        {paymentType === 'check' && (
          <label style={{ display: 'grid', gap: '0.2rem' }}>
            <span style={labelStyle}>Check #</span>
            <input
              type="text"
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
              aria-label="Check number"
              style={inputStyle}
            />
          </label>
        )}
        <label style={{ display: 'grid', gap: '0.2rem' }}>
          <span style={labelStyle}>Payable to</span>
          <input
            type="text"
            value={payableTo}
            onChange={(e) => setPayableTo(e.target.value)}
            aria-label="Payable to"
            style={inputStyle}
          />
        </label>
      </div>

      <label style={{ display: 'grid', gap: '0.2rem' }}>
        <span style={labelStyle}>Note (optional)</span>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Payout note"
          style={inputStyle}
        />
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="button" size="sm" disabled={saving} onClick={() => void submit()}>
          {saving ? 'Recording…' : 'Record payout'}
        </Button>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};
